import React, { useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import PatientList from './patientList';
import CreatePatientDialog from './CreatePatientDialog';
import AddTreatmentDialog from './AddTreatmentDialog';

/**
 * Container component that manages dialog state separately from patient data logic.
 * This prevents dialog open/close from triggering re-renders of PatientList.
 */
export default function PatientListContainer(props) {
  const token = useSelector((state) => state.auth.token);
  // Dialog state managed here, isolated from patient data
  const [createPatientDialogOpen, setCreatePatientDialogOpen] = useState(false);
  const [addTreatmentDialogOpen, setAddTreatmentDialogOpen] = useState(false);
  const [editTreatmentDialogOpen, setEditTreatmentDialogOpen] = useState(false);
  const [selectedPatientForTreatment, setSelectedPatientForTreatment] = useState(null);
  const [selectedTreatmentForEdit, setSelectedTreatmentForEdit] = useState(null);
  const addTreatmentSubmitHandlerRef = useRef(null);
  const editTreatmentSubmitHandlerRef = useRef(null);
  const deleteTreatmentHandlerRef = useRef(null);
  const archiveTreatmentHandlerRef = useRef(null);
  const reactivateTreatmentHandlerRef = useRef(null);

  const runWithJournalUnsavedGuard = useCallback((label, action) => {
    if (typeof action !== 'function') return;
    if (typeof window !== 'undefined') {
      const guard = window.__vpJournalUnsavedGuard;
      const isEditingMode = guard && typeof guard.isEditingMode === 'function'
        ? guard.isEditingMode()
        : false;
      if (isEditingMode && guard && typeof guard.request === 'function') {
        guard.request(label, action);
        return;
      }
    }
    action();
  }, []);

  // Stable callback references
  const handleOpenCreatePatientDialog = useCallback(() => {
    setCreatePatientDialogOpen(true);
  }, []);

  const handleCloseCreatePatientDialog = useCallback(() => {
    setCreatePatientDialogOpen(false);
  }, []);

  const handleOpenAddTreatmentDialog = useCallback((patient) => {
    runWithJournalUnsavedGuard('creating a new treatment', () => {
      setSelectedPatientForTreatment(patient);
      setAddTreatmentDialogOpen(true);
    });
  }, [runWithJournalUnsavedGuard]);

  const handleCloseAddTreatmentDialog = useCallback(() => {
    setAddTreatmentDialogOpen(false);
    setSelectedPatientForTreatment(null);
  }, []);

  const handleOpenEditTreatmentDialog = useCallback((patient, treatment) => {
    runWithJournalUnsavedGuard('renaming a treatment', () => {
      setSelectedPatientForTreatment(patient);
      setSelectedTreatmentForEdit(treatment || null);
      setEditTreatmentDialogOpen(true);
    });
  }, [runWithJournalUnsavedGuard]);

  const handleCloseEditTreatmentDialog = useCallback(() => {
    setEditTreatmentDialogOpen(false);
    setSelectedPatientForTreatment(null);
    setSelectedTreatmentForEdit(null);
  }, []);

  const handlePatientCreated = useCallback((data) => {
    setCreatePatientDialogOpen(false);
    // PatientList will handle the refresh via its onPatientCreated callback
  }, []);

  const handleTreatmentAdded = useCallback(() => {
    setAddTreatmentDialogOpen(false);
    setSelectedPatientForTreatment(null);
    // PatientList will handle the refresh via its onTreatmentAdded callback
  }, []);

  const handleRegisterEditTreatmentSubmit = useCallback((handler) => {
    editTreatmentSubmitHandlerRef.current = handler;
  }, []);

  const handleRegisterDeleteTreatment = useCallback((handler) => {
    deleteTreatmentHandlerRef.current = handler;
  }, []);

  const handleRegisterArchiveTreatment = useCallback((handler) => {
    archiveTreatmentHandlerRef.current = handler;
  }, []);

  const handleRegisterReactivateTreatment = useCallback((handler) => {
    reactivateTreatmentHandlerRef.current = handler;
  }, []);

  const handleRegisterAddTreatmentSubmit = useCallback((handler) => {
    addTreatmentSubmitHandlerRef.current = handler;
  }, []);

  return (
    <>
      <PatientList
        {...props}
        onOpenCreatePatientDialog={handleOpenCreatePatientDialog}
        onOpenAddTreatmentDialog={handleOpenAddTreatmentDialog}
        onDialogPatientCreated={handlePatientCreated}
        onDialogTreatmentAdded={handleTreatmentAdded}
        onRegisterAddTreatmentHandler={handleRegisterAddTreatmentSubmit}
        onOpenEditTreatmentDialog={handleOpenEditTreatmentDialog}
        onRegisterEditTreatmentHandler={handleRegisterEditTreatmentSubmit}
        onRegisterDeleteTreatmentHandler={handleRegisterDeleteTreatment}
        onRegisterArchiveTreatmentHandler={handleRegisterArchiveTreatment}
        onRegisterReactivateTreatmentHandler={handleRegisterReactivateTreatment}
      />

      <CreatePatientDialog
        open={createPatientDialogOpen}
        onClose={handleCloseCreatePatientDialog}
        token={token}
        profiles={props.profiles}
        onPatientCreated={handlePatientCreated}
      />

      <AddTreatmentDialog
        open={addTreatmentDialogOpen}
        onClose={handleCloseAddTreatmentDialog}
        token={token}
        patient={selectedPatientForTreatment}
        onReactivate={async (treatment) => {
          if (typeof reactivateTreatmentHandlerRef.current === 'function') {
            await reactivateTreatmentHandlerRef.current(selectedPatientForTreatment, treatment);
          }
        }}
        onSubmit={async (payload) => {
          if (typeof addTreatmentSubmitHandlerRef.current === 'function') {
            await addTreatmentSubmitHandlerRef.current(payload);
          }
          handleTreatmentAdded();
        }}
      />

      <AddTreatmentDialog
        open={editTreatmentDialogOpen}
        onClose={handleCloseEditTreatmentDialog}
        token={token}
        patient={selectedPatientForTreatment}
        mode="edit"
        treatment={selectedTreatmentForEdit}
        initialTreatmentName={selectedTreatmentForEdit?.name || selectedTreatmentForEdit?.display_name || selectedTreatmentForEdit?.diagnosis_title || selectedTreatmentForEdit?.diagnosis?.title || selectedTreatmentForEdit?.diagnosis?.diagnosis || ''}
        onSubmit={async (payload) => {
          if (typeof editTreatmentSubmitHandlerRef.current === 'function') {
            await editTreatmentSubmitHandlerRef.current(payload);
          }
          handleCloseEditTreatmentDialog();
        }}
        onDelete={async () => {
          if (typeof deleteTreatmentHandlerRef.current === 'function') {
            await deleteTreatmentHandlerRef.current(selectedPatientForTreatment, selectedTreatmentForEdit);
          }
        }}
        onArchive={async (treatment) => {
          if (typeof archiveTreatmentHandlerRef.current === 'function') {
            await archiveTreatmentHandlerRef.current(selectedPatientForTreatment, treatment);
          }
        }}
        onReactivate={async (treatment) => {
          if (typeof reactivateTreatmentHandlerRef.current === 'function') {
            await reactivateTreatmentHandlerRef.current(selectedPatientForTreatment, treatment);
          }
        }}
      />
    </>
  );
}
